import * as React from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { apiErrorMessage } from "@/lib/api";
import { createDeal, updateDeal } from "@/api/cpa";
import { listCampaignsPaged } from "@/api/campaigns";
import { dealSummary } from "@/lib/deals";
import { bpsToPercentInput, microsToUsdInput, parsePercentToBps, parseUsdToMicros } from "@/lib/money";
import type {
    CapPeriod,
    DealActionType,
    DealDto,
    DealSide,
    DealStatus,
    DealType,
    UpsertDealRequest,
} from "@/types/cpa";

export interface DealEditorDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Fixed by the page the dialog is opened from (advertiser vs affiliate detail). */
    side: DealSide;
    counterpartyId: number;
    /** null → create a new deal; set → edit that deal. */
    deal: DealDto | null;
    onSaved: () => void;
}

/** "12" → 12; anything non-integer or ≤ 0 → null. */
function parsePositiveInt(input: string): number | null {
    const cleaned = input.trim();
    if (cleaned === "") return null;
    const n = Number(cleaned);
    return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * Deal editor with progressive disclosure: action type + pricing model first,
 * then only the money fields that model needs; caps + test phase live behind
 * an "Advanced" expander. A live preview line re-renders the plain-English
 * sentence as the operator types — they confirm the sentence, not the fields.
 */
export function DealEditorDialog({
    open,
    onOpenChange,
    side,
    counterpartyId,
    deal,
    onSaved,
}: DealEditorDialogProps): React.ReactNode {
    const editing = deal != null;

    const [scope, setScope] = React.useState<string>("global");
    const [actionType, setActionType] = React.useState<DealActionType>("lead");
    const [dealType, setDealType] = React.useState<DealType>("cpa");
    const [cpaUsd, setCpaUsd] = React.useState("");
    const [revsharePct, setRevsharePct] = React.useState("");
    const [qualifyingUsd, setQualifyingUsd] = React.useState("");
    const [status, setStatus] = React.useState<DealStatus>("active");
    const [effectiveFrom, setEffectiveFrom] = React.useState("");
    const [effectiveTo, setEffectiveTo] = React.useState("");
    const [notes, setNotes] = React.useState("");
    const [advancedOpen, setAdvancedOpen] = React.useState(false);
    const [capActions, setCapActions] = React.useState("");
    const [capPeriod, setCapPeriod] = React.useState<CapPeriod>("month");
    const [testCapActions, setTestCapActions] = React.useState("");
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!open) return;
        setScope(deal?.campaignId != null ? String(deal.campaignId) : "global");
        setActionType(deal?.actionType ?? "lead");
        setDealType(deal?.dealType ?? "cpa");
        setCpaUsd(microsToUsdInput(deal?.cpaUsdMicros));
        setRevsharePct(bpsToPercentInput(deal?.revshareBps));
        setQualifyingUsd(microsToUsdInput(deal?.qualifyingDepositUsdMicros));
        setStatus(deal?.status ?? "active");
        setEffectiveFrom(deal?.effectiveFrom ? deal.effectiveFrom.slice(0, 10) : "");
        setEffectiveTo(deal?.effectiveTo ? deal.effectiveTo.slice(0, 10) : "");
        setNotes(deal?.notes ?? "");
        setAdvancedOpen(deal?.capActions != null || deal?.testCapActions != null);
        setCapActions(deal?.capActions != null ? String(deal.capActions) : "");
        setCapPeriod(deal?.capPeriod ?? "month");
        setTestCapActions(deal?.testCapActions != null ? String(deal.testCapActions) : "");
        setError(null);
    }, [open, deal]);

    const campaignsQuery = useQuery({
        queryKey: ["campaigns", "deal-scope"],
        queryFn: () => listCampaignsPaged({ size: 200, sort: "name,asc" }),
        enabled: open,
        staleTime: 60_000,
    });
    const campaigns = campaignsQuery.data?.content ?? [];

    const needsCpa = dealType === "cpa" || dealType === "hybrid";
    const needsShare = dealType === "revshare" || dealType === "hybrid";

    // Live plain-English preview — same builder the deal cards use.
    const preview = dealSummary({
        side,
        actionType,
        dealType,
        cpaUsdMicros: needsCpa ? parseUsdToMicros(cpaUsd) : null,
        revshareBps: needsShare ? parsePercentToBps(revsharePct) : null,
        qualifyingDepositUsdMicros:
            actionType === "ftd" && qualifyingUsd.trim() !== "" ? parseUsdToMicros(qualifyingUsd) : null,
        capActions: parsePositiveInt(capActions),
        capPeriod,
        testCapActions: parsePositiveInt(testCapActions),
        testConfirmedAt: deal?.testConfirmedAt ?? null,
    });

    const saveMut = useMutation({
        mutationFn: (req: UpsertDealRequest) =>
            editing ? updateDeal(deal.id, req) : createDeal(req),
        onSuccess: () => {
            toast.success(editing ? "Deal updated" : "Deal created");
            onOpenChange(false);
            onSaved();
        },
        onError: (err) => setError(apiErrorMessage(err, "Couldn't save the deal")),
    });

    const submit = (e: React.FormEvent): void => {
        e.preventDefault();
        setError(null);

        const cpaMicros = needsCpa ? parseUsdToMicros(cpaUsd) : null;
        if (needsCpa && cpaMicros == null) {
            setError("Enter a valid dollar amount for the flat fee.");
            return;
        }
        const shareBps = needsShare ? parsePercentToBps(revsharePct) : null;
        if (needsShare && shareBps == null) {
            setError("Enter a valid revenue share between 0 and 100 percent.");
            return;
        }
        let qualifyingMicros: number | null = null;
        if (actionType === "ftd" && qualifyingUsd.trim() !== "") {
            qualifyingMicros = parseUsdToMicros(qualifyingUsd);
            if (qualifyingMicros == null) {
                setError("Enter a valid dollar amount for the minimum deposit, or leave it blank.");
                return;
            }
        }
        const capInt = capActions.trim() === "" ? null : parsePositiveInt(capActions);
        if (capActions.trim() !== "" && capInt == null) {
            setError("The cap must be a whole number greater than zero.");
            return;
        }
        const testInt = testCapActions.trim() === "" ? null : parsePositiveInt(testCapActions);
        if (testCapActions.trim() !== "" && testInt == null) {
            setError("The test phase size must be a whole number greater than zero.");
            return;
        }
        if (effectiveFrom && effectiveTo && effectiveTo < effectiveFrom) {
            setError("The end date must be on or after the start date.");
            return;
        }

        const req: UpsertDealRequest = {
            // Side + counterparty are immutable after creation — omit on edit.
            ...(editing
                ? {}
                : {
                      side,
                      advertiserId: side === "advertiser" ? counterpartyId : undefined,
                      affiliateId: side === "affiliate" ? counterpartyId : undefined,
                  }),
            campaignId: scope === "global" ? null : Number(scope),
            actionType,
            dealType,
            cpaUsdMicros: cpaMicros,
            revshareBps: shareBps,
            qualifyingDepositUsdMicros: qualifyingMicros,
            capActions: capInt,
            capPeriod: capInt != null ? capPeriod : null,
            testCapActions: testInt,
            status,
            effectiveFrom: effectiveFrom ? new Date(`${effectiveFrom}T00:00:00Z`).toISOString() : null,
            effectiveTo: effectiveTo ? new Date(`${effectiveTo}T23:59:59Z`).toISOString() : null,
            notes: notes.trim() || null,
        };
        saveMut.mutate(req);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="deal-editor-dialog">
                <DialogHeader>
                    <DialogTitle>{editing ? "Edit deal" : "New deal"}</DialogTitle>
                    <DialogDescription>
                        {side === "advertiser"
                            ? "Money in — what this advertiser pays us for each action."
                            : "Money out — what we pay this affiliate for each action."}
                    </DialogDescription>
                </DialogHeader>
                <form className="space-y-4" onSubmit={submit}>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label>Applies to</Label>
                            <Select value={scope} onValueChange={setScope} disabled={editing}>
                                <SelectTrigger data-testid="deal-scope-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="global">All campaigns (global default)</SelectItem>
                                    {/* Keep the current scope selectable even if it's outside the fetched page. */}
                                    {deal?.campaignId != null &&
                                        !campaigns.some((c) => c.id === deal.campaignId) && (
                                            <SelectItem value={String(deal.campaignId)}>
                                                Campaign #{deal.campaignId}
                                            </SelectItem>
                                        )}
                                    {campaigns.map((c) => (
                                        <SelectItem key={c.id} value={String(c.id)}>
                                            {c.name} · {c.domainHostname}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {editing && (
                                <p className="text-[11px] text-muted-foreground">
                                    Scope is fixed after creation — end this deal and create a new one to change it.
                                </p>
                            )}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Status</Label>
                            <Select value={status} onValueChange={(v) => setStatus(v as DealStatus)}>
                                <SelectTrigger data-testid="deal-status-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="draft">Draft</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="paused">Paused</SelectItem>
                                    <SelectItem value="ended">Ended</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label>Paid action</Label>
                            <Select value={actionType} onValueChange={(v) => setActionType(v as DealActionType)}>
                                <SelectTrigger data-testid="deal-action-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="click">Click</SelectItem>
                                    <SelectItem value="lead">Lead</SelectItem>
                                    <SelectItem value="ftd">First-time deposit (FTD)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>Pricing</Label>
                            <Select value={dealType} onValueChange={(v) => setDealType(v as DealType)}>
                                <SelectTrigger data-testid="deal-type-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="cpa">Flat amount per action</SelectItem>
                                    <SelectItem value="revshare">Revenue share</SelectItem>
                                    <SelectItem value="hybrid">Flat amount + revenue share</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Only the money fields the chosen pricing model needs. */}
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {needsCpa && (
                            <div className="space-y-1.5">
                                <Label htmlFor="deal-cpa">Flat amount ($ per action)</Label>
                                <Input
                                    id="deal-cpa"
                                    inputMode="decimal"
                                    placeholder="e.g. 250"
                                    value={cpaUsd}
                                    onChange={(e) => setCpaUsd(e.target.value)}
                                    data-testid="deal-cpa-input"
                                />
                            </div>
                        )}
                        {needsShare && (
                            <div className="space-y-1.5">
                                <Label htmlFor="deal-share">
                                    {actionType === "ftd" ? "Share of deposits (%)" : "Share of revenue (%)"}
                                </Label>
                                <Input
                                    id="deal-share"
                                    inputMode="decimal"
                                    placeholder="e.g. 5"
                                    value={revsharePct}
                                    onChange={(e) => setRevsharePct(e.target.value)}
                                    data-testid="deal-share-input"
                                />
                            </div>
                        )}
                        {actionType === "ftd" && (
                            <div className="space-y-1.5">
                                <Label htmlFor="deal-qualifying">Minimum qualifying deposit ($)</Label>
                                <Input
                                    id="deal-qualifying"
                                    inputMode="decimal"
                                    placeholder="Optional — e.g. 20"
                                    value={qualifyingUsd}
                                    onChange={(e) => setQualifyingUsd(e.target.value)}
                                    data-testid="deal-qualifying-input"
                                />
                                <p className="text-[11px] text-muted-foreground">
                                    Deposits below this amount don't count as an FTD.
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="deal-from">Starts</Label>
                            <Input
                                id="deal-from"
                                type="date"
                                value={effectiveFrom}
                                onChange={(e) => setEffectiveFrom(e.target.value)}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="deal-to">Ends</Label>
                            <Input
                                id="deal-to"
                                type="date"
                                value={effectiveTo}
                                onChange={(e) => setEffectiveTo(e.target.value)}
                            />
                            <p className="text-[11px] text-muted-foreground">Leave both blank for an open-ended deal.</p>
                        </div>
                    </div>

                    {/* Caps + test phase — most deals don't need them. */}
                    <div className="rounded-md border border-border">
                        <button
                            type="button"
                            onClick={() => setAdvancedOpen((v) => !v)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium hover:bg-accent/40"
                            data-testid="deal-advanced-toggle"
                        >
                            {advancedOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            Advanced — caps &amp; test phase
                        </button>
                        {advancedOpen && (
                            <div className="space-y-3 border-t border-border p-3">
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="deal-cap">Cap paid actions at</Label>
                                        <Input
                                            id="deal-cap"
                                            inputMode="numeric"
                                            placeholder="e.g. 30 — blank for no cap"
                                            value={capActions}
                                            onChange={(e) => setCapActions(e.target.value)}
                                            data-testid="deal-cap-input"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label>Per</Label>
                                        <Select
                                            value={capPeriod}
                                            onValueChange={(v) => setCapPeriod(v as CapPeriod)}
                                            disabled={capActions.trim() === ""}
                                        >
                                            <SelectTrigger data-testid="deal-cap-period-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="day">Day</SelectItem>
                                                <SelectItem value="week">Week</SelectItem>
                                                <SelectItem value="month">Month</SelectItem>
                                                <SelectItem value="total">Deal lifetime (total)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="deal-test-cap">Test phase — hold payment on the first</Label>
                                    <Input
                                        id="deal-test-cap"
                                        inputMode="numeric"
                                        placeholder="e.g. 10 — blank for no test phase"
                                        value={testCapActions}
                                        onChange={(e) => setTestCapActions(e.target.value)}
                                        data-testid="deal-test-cap-input"
                                    />
                                    <p className="text-[11px] text-muted-foreground">
                                        The first N actions accrue on hold until you confirm the traffic quality
                                        ("Confirm test passed" on the deal card).
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="deal-notes">Notes</Label>
                        <Textarea
                            id="deal-notes"
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Anything the next operator should know about this agreement"
                        />
                    </div>

                    {/* Live preview — the operator confirms the sentence, not the fields. */}
                    <div
                        className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm"
                        data-testid="deal-preview-line"
                    >
                        {preview}
                    </div>

                    {error && (
                        <p className="text-sm text-destructive" data-testid="deal-editor-error">
                            {error}
                        </p>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saveMut.isPending}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={saveMut.isPending} data-testid="deal-editor-submit">
                            {saveMut.isPending ? <Spinner size="sm" /> : editing ? "Save changes" : "Create deal"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
